import React, { useState, useEffect } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import LoginModal from "../Main/LoginModal";
import emptyCartImg from "../../Assets/empty.jpg";
import { createOrder, getTimeSlots } from "../../Api/Api";
import { FaShoppingCart, FaMapMarkerAlt, FaCalendarAlt, FaCreditCard, FaLock, FaCheckCircle, FaCrosshairs, FaSearch } from "react-icons/fa";

const CartPage = ({ cartItems = [], onUpdateCart }) => {
  const [items, setItems] = useState([]);
  const [step, setStep] = useState("cart"); // "cart" | "address" | "schedule" | "payment"
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  });
  const [addressInput, setAddressInput] = useState("");
  const [savedAddress, setSavedAddress] = useState(() => ({
    name: user?.name || "",
    mobile: user?.mobile || "",
    street: "",
    landmark: "",
  }));
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [timesSlots, setTimeSlots] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLocating, setIsLocating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("cartItems")) || [];
    setItems(cartItems && cartItems.length ? cartItems : stored);

    if (!localStorage.getItem("authToken")) {
      setShowLogin(true);
    }
  }, [cartItems]);

  const updateCartState = (updated) => {
    setItems(updated);
    localStorage.setItem("cartItems", JSON.stringify(updated));
    window.dispatchEvent(new Event("cartUpdated"));
    if (onUpdateCart) onUpdateCart(updated);
  };

  useEffect(() => {
    getTimeSlots()
      .then((res) => setTimeSlots(Array.isArray(res) ? res : []))
      .catch((err) => console.error("Failed to fetch timeslots", err));
  }, []);

  const handleIncreaseQty = (index) => {
    const updated = [...items];
    updated[index].quantity = (updated[index].quantity || 1) + 1;
    updateCartState(updated);
  };

  const handleDecreaseQty = (index) => {
    const updated = [...items];
    if ((updated[index].quantity || 1) <= 1) {
      updated.splice(index, 1);
    } else {
      updated[index].quantity = (updated[index].quantity || 1) - 1;
    }
    updateCartState(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    updateCartState(updated);
  };

  const subtotal = items.reduce(
    (acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 1),
    0
  );

  const handleConfirmBooking = async () => {
    if (!selectedTime?.id) {
      alert("Please select a time slot");
      return;
    }

    if (!user?.id) {
      alert("Please login again");
      setShowLogin(true);
      return;
    }

    const orderPayload = {
      services: items.map((i) => i.id).join(","),
      landmark: savedAddress.landmark || "",
      street: savedAddress.street || "",
      mobile: savedAddress.mobile || user.mobile || "",
      user_name: savedAddress.name || user.name || "",
      address: addressInput || localStorage.getItem("userLocation") || "Bengaluru, KA",
      delivery_date: selectedDate,
      timeslot_id: selectedTime.id,
      payment_method: paymentMethod,
      sub_total: Number(subtotal),
      tax: 0,
      total: Number(subtotal),
      email: user.email,
      user_id: user.id,
    };

    try {
      const result = await createOrder(orderPayload);
      if (paymentMethod === "cod") {
        alert("Order placed successfully! Please keep cash ready.");
        localStorage.removeItem("cartItems");
        navigate("/order-success");
      } else {
        if (result.success && result.payu) {
          const payu = result.payu;
          const form = document.createElement("form");
          form.method = "POST";
          form.action = payu.payu_url;

          Object.keys(payu).forEach((key) => {
            if (key !== "payu_url") {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = key;
              input.value = payu[key];
              form.appendChild(input);
            }
          });

          document.body.appendChild(form);
          form.submit();
        } else {
          alert("Payment initiation failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Order creation failed:", error);
      alert("Unable to place your order. Please try again.");
    }
  };

  const handleSearchLocation = async (value = searchInput) => {
    if (value.trim().length > 2) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&countrycodes=in&addressdetails=1&q=${encodeURIComponent(value)}`
        );
        const data = await response.json();
        setSearchResults(data.slice(0, 5));
      } catch {
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Location is not supported by this browser.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${coords.latitude}&lon=${coords.longitude}`
          );
          const data = await response.json();
          const address = data.address || {};
          setAddressInput(data.display_name || "");
          setSearchInput(data.display_name || "");
          setSavedAddress((prev) => ({
            ...prev,
            street: address.road || address.neighbourhood || "",
            landmark: address.amenity || address.suburb || "",
          }));
          setSearchResults([]);
        } catch {
          alert("We could not read your current address. Please search manually.");
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setIsLocating(false);
        alert("Location permission was denied. Please search for your address manually.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const handleSelectLocation = (location) => {
    setAddressInput(location.display_name);
    setSavedAddress((prev) => ({
      ...prev,
      street: location.address?.road || "",
      landmark: location.address?.amenity || "",
    }));
    setSearchResults([]);
  };

  function formatTimeToIST(time) {
    if (!time) return "10:00 AM";
    try {
      const date = new Date(`1970-01-01T${time}Z`);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return time;
    }
  }

  const checkoutSteps = [
    { key: "cart", label: "Cart", icon: FaShoppingCart },
    { key: "address", label: "Address", icon: FaMapMarkerAlt },
    { key: "schedule", label: "Schedule", icon: FaCalendarAlt },
    { key: "payment", label: "Payment", icon: FaCreditCard },
  ];
  const currentStepNum = checkoutSteps.findIndex(({ key }) => key === step) + 1;
  const canContinueToSchedule = [
    addressInput,
    savedAddress.name,
    savedAddress.mobile,
    savedAddress.street,
  ].every((value) => typeof value === "string" && value.trim().length > 0);

  const BookingSummary = () => (
    <aside className="h-max rounded-xl border border-gray-200 bg-white p-5 shadow-[0_5px_18px_rgba(0,0,0,0.05)] lg:sticky lg:top-24">
      <h3 className="border-b border-gray-100 pb-4 text-lg font-semibold tracking-tight text-[#17171a]">
        Booking Summary
      </h3>
      <div className="divide-y divide-gray-100">
        {items.map((item, index) => (
          <div key={`${item.id}-${index}`} className="flex justify-between gap-3 py-3 text-xs">
            <div className="min-w-0">
              <p className="truncate font-semibold text-[#17171a]">{item.name}</p>
              <p className="mt-1 text-[11px] capitalize text-gray-400">
                {item.variant && item.variant !== "none" ? `${item.variant} package` : "Classic package"} · {item.quantity || 1} unit{(item.quantity || 1) > 1 ? "s" : ""}
              </p>
            </div>
            <span className="shrink-0 font-semibold text-[#17171a]">
              ₹{((Number(item.price) || 0) * (Number(item.quantity) || 1)).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
      {(addressInput || selectedDate || selectedTime) && (
        <div className="space-y-2 border-y border-gray-100 py-4 text-xs text-gray-600">
          {addressInput && <p><FaMapMarkerAlt className="mr-2 inline text-[#AF7D23]" />{addressInput}</p>}
          {selectedDate && <p><FaCalendarAlt className="mr-2 inline text-[#0A4D3C]" />{selectedDate}{selectedTime ? ` · ${formatTimeToIST(selectedTime.start_time)}` : ""}</p>}
        </div>
      )}
      <div className="space-y-3 pt-4 text-xs text-gray-600">
        <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Taxes &amp; Fees</span><span>₹0.00</span></div>
        <div className="flex justify-between border-t border-gray-100 pt-4 text-base font-bold text-[#17171a]"><span>Total</span><span>₹{subtotal.toFixed(2)}</span></div>
      </div>
      <p className="mt-5 flex items-center justify-center gap-2 text-[10px] font-semibold text-gray-400"><FaLock size={9} /> Secure checkout</p>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] px-4 py-8 sm:px-6 lg:px-12 lg:py-24">
      <div className="mx-auto max-w-6xl space-y-8">
        
        {/* TOP STEPPER PROGRESS BAR (MATCHING SCREENSHOT 100%) */}
        <div className="mx-auto max-w-2xl px-2 pt-1">
          <div className="relative">
            <div className="absolute left-[12%] right-[12%] top-4 h-px bg-gray-300" />
            <div
              className="absolute left-[12%] top-4 h-px bg-[#0D004C] transition-all duration-500"
              style={{ width: `${Math.max(0, (currentStepNum - 1) * 25)}%` }}
            />
            <div className="relative z-10 flex items-start justify-between">
              {checkoutSteps.map(({ key, label, icon: Icon }, index) => {
                const stepNumber = index + 1;
                const isComplete = currentStepNum > stepNumber;
                const isActive = currentStepNum === stepNumber;
                const isClickable = key === "cart" || (key === "address" && items.length > 0);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!isClickable}
                    onClick={() => isClickable && setStep(key)}
                    className="flex w-16 flex-col items-center gap-1.5 text-center disabled:cursor-default sm:w-20"
                  >
                    <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-[11px] font-bold transition-all sm:h-9 sm:w-9 ${
                      isComplete ? "border-[#0D004C] bg-[#0D004C] text-white" :
                      isActive ? "border-[#0D004C] bg-white text-[#0D004C] ring-4 ring-[#0D004C]/10" :
                      "border-gray-300 bg-[#f7f8fa] text-gray-400"
                    }`}>
                      {isComplete ? <FaCheckCircle size={13} /> : <Icon size={13} />}
                    </span>
                    <span className={`text-[10px] font-semibold sm:text-xs ${isActive || isComplete ? "text-[#17171a]" : "text-gray-400"}`}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* STEP 1: REVIEW YOUR CART */}
        {step === "cart" && (
          <div>
            <h1 className="mb-8 text-3xl font-semibold tracking-[-0.04em] text-[#17171a] sm:text-5xl">
              Review your cart
            </h1>

            {items.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] max-w-md mx-auto space-y-4">
                <img src={emptyCartImg} alt="Empty Cart" className="h-44 w-auto mx-auto object-contain opacity-80" />
                <h3 className="text-base font-bold text-gray-900">Your cart is empty</h3>
                <p className="text-xs text-gray-400">Looks like you haven't added any home services to your cart yet.</p>
                <button
                  onClick={() => navigate("/")}
                    className="rounded-lg bg-[#0D004C] px-8 py-3 text-xs font-bold text-white shadow transition hover:bg-[#1a1460]"
                >
                  Explore Services →
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* LEFT COLUMN: CART ITEMS LIST (2/3 width) */}
                <div className="space-y-6 lg:col-span-2">
                  {items.map((item, idx) => {
                    const qty = item.quantity || 1;
                    const itemTotal = (Number(item.price) || 0) * qty;

                    return (
                      <div
                        key={idx}
                        className="flex flex-col items-center justify-between gap-6 rounded-xl border border-gray-200 bg-white p-5 shadow-[0_3px_12px_rgba(0,0,0,0.035)] transition-all hover:shadow-[0_8px_20px_rgba(0,0,0,0.07)] sm:flex-row sm:p-6"
                      >
                        {/* Service Thumbnail */}
                        <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl bg-gray-100 sm:w-24">
                          <img
                            src={
                              item.image?.startsWith("http")
                                ? item.image
                                : `http://skishore.in/api/public/${item.image}`
                            }
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Middle Details & Stepper */}
                        <div className="flex-1 w-full space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="text-lg font-semibold tracking-tight text-[#17171a]">{item.name}</h4>
                              <p className="text-xs text-gray-400 font-medium capitalize mt-0.5">
                                {item.variant && item.variant !== "none" ? `${item.variant} Package` : "Classic Package"}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(idx)}
                              className="text-gray-400 hover:text-red-500 transition p-1 sm:hidden"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Quantity Stepper Pill */}
                          <div className="inline-flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-800">
                            <button
                              onClick={() => handleDecreaseQty(idx)}
                              className="hover:text-red-600 text-sm font-bold leading-none"
                            >
                              −
                            </button>
                            <span>{qty}</span>
                            <button
                              onClick={() => handleIncreaseQty(idx)}
                              className="hover:text-green-600 text-sm font-bold leading-none"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Right Total & Trash */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto shrink-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                          <button
                            onClick={() => handleRemoveItem(idx)}
                            className="hidden sm:block text-gray-400 hover:text-red-500 transition mb-4"
                            title="Remove item"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                          <div className="text-xl font-bold text-[#17171a]">
                            ₹{itemTotal.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* RIGHT COLUMN: ORDER SUMMARY BOX (1/3 width matching screenshot) */}
                <div className="lg:col-span-1">
                  <div className="sticky top-24 h-max space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-[0_5px_18px_rgba(0,0,0,0.05)] sm:p-7">
                    <h3 className="text-xl font-semibold tracking-tight text-[#17171a]">
                      Order Summary
                    </h3>

                    <div className="space-y-3 text-xs sm:text-sm">
                      <div className="flex justify-between text-gray-600 font-medium">
                        <span>Subtotal ({items.length} {items.length === 1 ? "item" : "items"})</span>
                        <span className="font-bold text-gray-900">₹{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 font-medium">
                        <span>Taxes & Fees</span>
                        <span className="font-bold text-gray-900">₹0.00</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4 flex justify-between items-end">
                      <span className="text-base font-bold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-[#17171a]">
                        ₹{subtotal.toFixed(2)}
                      </span>
                    </div>

                    <button
                      onClick={() => setStep("address")}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-[#0D004C] py-3.5 text-xs font-bold tracking-wide text-white shadow-md transition-all hover:bg-[#1a1460]"
                    >
                      <span>Proceed to Address →</span>
                    </button>

                    <div className="text-[11px] text-gray-400 font-semibold text-center flex items-center justify-center gap-1.5 pt-1">
                      <FaLock size={10} />
                      <span>Secure Checkout</span>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* STEP 2: ADDRESS SELECTION */}
        {step === "address" && (
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-[0_5px_18px_rgba(0,0,0,0.05)] sm:p-8">
              <div className="mb-7 flex items-center gap-3 border-b border-gray-100 pb-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#fff4e5] text-[#AF7D23]"><FaMapMarkerAlt size={15} /></span>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-[#17171a]">Service Location</h2>
                  <p className="mt-1 text-xs text-gray-500">Where should our professional meet you?</p>
                </div>
              </div>

              <div className="space-y-6">

            {/* Location Search Input */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Search Area or Pincode</label>
              <div className="relative">
                <FaSearch className="absolute left-4 top-3.5 text-gray-400" size={12} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    handleSearchLocation(value);
                  }}
                  placeholder="Search area, landmark or pincode..."
                  className="w-full rounded-xl border border-gray-200 bg-[#fffaf0] py-3 pl-10 pr-32 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
                />
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={isLocating}
                  className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded-lg px-2 py-1.5 text-[10px] font-bold text-[#0D004C] transition hover:bg-[#fff0c7] disabled:opacity-50"
                >
                  <FaCrosshairs className={isLocating ? "animate-spin" : "text-[#AF7D23]"} size={12} />
                  {isLocating ? "Locating..." : "Use current"}
                </button>
              </div>
              {searchResults.length > 0 && (
                <ul className="border border-gray-200 rounded-xl mt-2 max-h-40 overflow-y-auto divide-y divide-gray-100 bg-white shadow-sm">
                  {searchResults.map((location, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectLocation(location)}
                      className="p-3 text-xs cursor-pointer hover:bg-gray-50 transition text-gray-700 font-medium"
                    >
                      {location.display_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Full Address */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Full Address / Flat No.</label>
              <input
                type="text"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                placeholder="Door No, Building Name, Flat 4B..."
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Street / Area</label>
                <input
                  type="text"
                  placeholder="e.g. MG Road, Gandhinagar"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
                  onChange={(e) =>
                    setSavedAddress((prev) => ({ ...prev, street: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Landmark</label>
                <input
                  type="text"
                  placeholder="e.g. Near Bus Stand"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
                  onChange={(e) =>
                    setSavedAddress((prev) => ({ ...prev, landmark: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={savedAddress.name}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
                  onChange={(e) =>
                    setSavedAddress((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  value={savedAddress.mobile}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
                  onChange={(e) =>
                    setSavedAddress((prev) => ({ ...prev, mobile: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Navigation Actions */}
            <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center">
              <button
                onClick={() => setStep("cart")}
                className="text-xs font-bold text-gray-600 hover:underline"
              >
                ← Back to Cart
              </button>
              <button
                onClick={() => setStep("schedule")}
                disabled={!canContinueToSchedule}
                className="rounded-lg bg-black px-8 py-3 text-xs font-bold text-white shadow transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next: Schedule Time →
              </button>
            </div>
            </div>
            </div>
            <BookingSummary />
          </div>
        )}

        {/* STEP 3: SCHEDULE TIME */}
        {step === "schedule" && (
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-[0_5px_18px_rgba(0,0,0,0.05)] sm:p-8">
            <div className="mb-7 flex items-center gap-3 border-b border-gray-100 pb-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#eaf7f3] text-[#0A4D3C]"><FaCalendarAlt size={15} /></span>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-[#17171a]">Select a schedule</h2>
                <p className="mt-1 text-xs text-gray-500">Choose a date and time for your appointment.</p>
              </div>
            </div>

            {/* Date Picker */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Choose Service Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-xs outline-none focus:border-[#AF7D23] focus:ring-2 focus:ring-[#AF7D23]/20"
              />
            </div>

            {/* Time Slots */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Select Preferred Time Slot</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {timesSlots.map((slot, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedTime(slot)}
                    disabled={!selectedDate}
                    className={`px-4 py-3 rounded-xl text-xs font-bold border transition-all ${
                      selectedTime?.id === slot.id
                        ? "border-[#0D004C] bg-[#0D004C] text-white shadow"
                        : "border-gray-200 bg-[#eef2ff] text-[#0D004C] hover:border-[#cbd5ff] hover:bg-white"
                    } ${!selectedDate ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    {formatTimeToIST(slot.start_time)}
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-100">
              <button
                onClick={() => setStep("address")}
                className="text-xs font-bold text-gray-600 hover:underline"
              >
                ← Back to Address
              </button>
              <button
                onClick={() => setStep("payment")}
                disabled={!selectedTime || !selectedDate}
                className="rounded-lg bg-[#0D004C] px-8 py-3 text-xs font-bold text-white shadow transition hover:bg-[#1a1460] disabled:opacity-50"
              >
                Next: Payment →
              </button>
            </div>
            </div>
            <BookingSummary />
          </div>
        )}

        {/* STEP 4: PAYMENT */}
        {step === "payment" && (
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="max-w-xl space-y-7 rounded-xl border border-gray-200 bg-white p-6 shadow-[0_5px_18px_rgba(0,0,0,0.05)] sm:p-8">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#eef2ff] text-[#0D004C]"><FaCreditCard size={16} /></span>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-[#17171a]">Payment method</h2>
                <p className="mt-1 text-xs text-gray-500">Choose how you would like to pay.</p>
              </div>
            </div>

            {/* Booking Summary Box */}
            <div className="rounded-xl border border-gray-200 bg-[#fafafa] p-4 text-xs text-gray-600">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold text-gray-400">Booking details</span>
                <span className="font-bold text-[#0D004C]">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="grid gap-2 border-t border-gray-200 pt-3 sm:grid-cols-2">
                <p><strong>Address:</strong> {addressInput || "Bengaluru, KA"}</p>
                <p><strong>Contact:</strong> {savedAddress.name || user?.name} ({savedAddress.mobile || user?.mobile})</p>
                <p className="sm:col-span-2"><strong>Appointment:</strong> {selectedDate} · {selectedTime ? formatTimeToIST(selectedTime.start_time) : "10:00 AM"}</p>
              </div>
            </div>

            {/* Payment Options */}
            <div className="space-y-3">
              <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${paymentMethod === "cod" ? "border-[#e3c477] bg-[#fffaf0] ring-2 ring-[#e3c477]/20" : "border-gray-200 bg-white hover:border-[#e3c477]"}`}>
                <input
                  className="mt-1 accent-[#AF7D23]"
                  type="radio"
                  name="payment"
                  value="cod"
                  checked={paymentMethod === "cod"}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#fff0c7] text-base">💵</span>
                <span><strong className="block text-sm text-[#17171a]">Cash on Delivery</strong><small className="mt-1 block text-xs text-gray-500">Pay after the service is completed.</small></span>
              </label>

              <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${paymentMethod === "online" ? "border-[#0D004C] bg-[#eef2ff] ring-2 ring-[#0D004C]/10" : "border-gray-200 bg-white hover:border-[#0D004C]"}`}>
                <input
                  className="mt-1 accent-[#0D004C]"
                  type="radio"
                  name="payment"
                  value="online"
                  checked={paymentMethod === "online"}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#dfe5ff] text-base">💳</span>
                <span><strong className="block text-sm text-[#17171a]">Online Payment</strong><small className="mt-1 block text-xs text-gray-500">Secure checkout with UPI or card.</small></span>
              </label>
            </div>

            <div className="pt-2 flex flex-col gap-3">
              <button
                onClick={handleConfirmBooking}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0D004C] py-4 text-xs font-bold text-white shadow-md transition hover:bg-[#1a1460]"
              >
                <FaCheckCircle size={14} />
                <span>{paymentMethod === "cod" ? "Confirm booking" : `Pay ₹${subtotal.toFixed(2)}`}</span>
              </button>

              <button
                onClick={() => setStep("schedule")}
                className="text-xs font-bold text-gray-500 hover:underline text-center"
              >
                ← Back to Schedule
              </button>
            </div>
            </div>
            <BookingSummary />
          </div>
        )}

      </div>

      {showLogin && (
        <LoginModal onClose={() => setShowLogin(false)} cart={showLogin} />
      )}
    </div>
  );
};

export default CartPage;
